import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Row, Col, Card, Table, Button, Spinner } from "react-bootstrap";
import PageTitle from "../../Components/PageTitle";
import { useTranslation } from "react-i18next";
import { getMasterHydrantList, getAllUsersRoleWiseList } from "../../redux/actions/MasterActions";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { logout } from "../../redux/actions/AuthActions";
import useDebounce from "../../hooks/useDebounce";
import { getDisplayDateTimeFormat } from "../../util/generalUtills";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-dropdown-select";
import Preloader from "../../Components/Preloader";
import { getHydrantLiveList } from "../../redux/actions/HydrantActions";
import Chart from "react-apexcharts";
import { baseURL } from "../../axios";
import CPPlusLive from "../../Components/CPPlusLive";
import { FaSyncAlt } from "react-icons/fa";
import { useSocketContext } from "../../Context/SocketContext";
import "./HydrantLiveStatusReport.css";

// The camera photo itself only changes ~once a minute; poll its size (not the whole image) on this
// cadence and only force an <img> reload when the size actually differs from what we last saw.
const CAMERA_PHOTO_CHECK_INTERVAL_MS = 15000;

const HydrantLiveStatusReport = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    AuthReducer: { auth, errors },
    hydrantReducer: {
      hydrantLists,
      hydrantStatusList,
      summary,
      loadingHydrant,
    },
    masterReducer: {
      masterHydrantLists,
      masterUsersRoleWiseLists,
      loadingMaster,
    },
  } = useSelector((state) => state);

  const [page] = useState(1);
  const [paginate] = useState(10000);
  const [search] = useState("");
  const [sort] = useState("desc");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [jenId, setJenId] = useState("");
  const [selectedJen, setSelectedJen] = useState([]);
  const [hydrantId, setHydrantId] = useState([]);
  const [duration] = useState(60);
  const [graphHeader, setGraphHeader] = useState([]);
  const [graphHeaderEnd, setGraphHeaderEnd] = useState([]);
  const [graphDataTotalVolume, setGraphDataTotalVolume] = useState([]);
  const [graphDataEndVolume, setGraphDataEndVolume] = useState([]);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [cameraCacheBust, setCameraCacheBust] = useState(() => Date.now());
  const [cameraViewMode, setCameraViewMode] = useState("photo");
  const { socket } = useSocketContext();

  const handleDateChange = (date) => setSelectedDate(date);

  const jenOptions = useMemo(() => {
    if (!Array.isArray(masterUsersRoleWiseLists)) return [];
    return masterUsersRoleWiseLists
      .filter((item) => item && item.id)
      .map((item) => ({
        ...item,
        name: item.name || item.userId || item.id,
      }));
  }, [masterUsersRoleWiseLists]);

  const hydrantOptions = useMemo(() => {
    if (!Array.isArray(masterHydrantLists)) return [];
    return masterHydrantLists
      .filter((item) => item && item.hydrantId)
      .map((item) => ({
        ...item,
        id: item.hydrantId,
        name: item.name || item.hydrantId,
      }));
  }, [masterHydrantLists]);

  useEffect(() => {
    if (auth?.accessToken) {
      dispatch(getAllUsersRoleWiseList(auth?.accessToken, "JUNIOR_ENGINEER"));
    }
  }, [auth?.accessToken, dispatch]);

  useEffect(() => {
    if (jenId) {
      dispatch(getMasterHydrantList(auth?.accessToken, jenId));
    }
    setHydrantId([]);
    if (!jenId) setSelectedJen([]);
  }, [jenId, dispatch, auth?.accessToken]);

  const fetchLiveTracking = useCallback(() => {
    setLastRefreshedAt(Date.now());
    dispatch(
      getHydrantLiveList(
        auth?.accessToken,
        paginate,
        page,
        search,
        sort,
        hydrantId[0]?.hydrantId || hydrantId[0]?.id,
        duration,
        selectedDate
      )
    );
  }, [auth?.accessToken, dispatch, paginate, page, search, sort, hydrantId, duration, selectedDate]);

  // Only API call: the initial load (and whenever filters change).
  useEffect(() => {
    fetchLiveTracking();
  }, [fetchLiveTracking]);

  // Manual reload button: while the socket is connected, ask it for a fresh reading instead of
  // hitting the API — falls back to the API only when the socket is disconnected.
  const handleReload = useCallback(() => {
    if (hydrantId.length === 0) return;
    if (socket?.connected) {
      socket.emit("requestHydrantStatus", {
        hydrantId: hydrantId[0]?.hydrantId || hydrantId[0]?.id,
      });
    } else {
      fetchLiveTracking();
    }
  }, [socket, hydrantId, fetchLiveTracking]);

  // Every update after that comes straight from the socket — the backend pushes the fully-shaped
  // summary/hydrantStatusList payload as soon as it processes an MQTT message, no API call here at all.
  useEffect(() => {
    if (!socket) return;

    const handleLiveStatusUpdate = (data) => {
      if (hydrantId.length === 0) return;
      const selectedHydrantId = hydrantId[0]?.hydrantId || hydrantId[0]?.id;
      // Ignore pushes for a hydrant other than the one currently selected on screen.
      if (data?.hydrantId && selectedHydrantId && data.hydrantId !== selectedHydrantId) return;

      setLastRefreshedAt(Date.now());
      dispatch({
        type: "HYDRANT_LIVE_STATUS_SOCKET_UPDATE",
        hydrantStatusList: data?.hydrantStatusList,
        summary: data?.summary,
      });
    };

    socket.on("hydrantTelemetryUpdate", handleLiveStatusUpdate);

    return () => {
      socket.off("hydrantTelemetryUpdate", handleLiveStatusUpdate);
    };
  }, [socket, hydrantId, dispatch]);

  // The camera photo URL itself doesn't change on every socket push (hydrantStatusList updates
  // every ~2s now), only the file behind it does — roughly once a minute. Instead of reloading the
  // <img> on every push, periodically check the photo's size and only reload when it actually differs.
  const lastPhotoSizeRef = useRef(null);
  const currentPhotoUrl = hydrantStatusList?.[0]?.camera?.lastPhotoUrl;
  useEffect(() => {
    const url = currentPhotoUrl;
    if (!url) return;

    let cancelled = false;
    let consecutiveFailures = 0;

    const checkForNewPhoto = async () => {
      try {
        const response = await fetch(url, { method: "HEAD", cache: "no-store" });
        const size = response.headers.get("content-length");
        consecutiveFailures = 0;
        if (cancelled || size == null) return;
        if (lastPhotoSizeRef.current !== null && lastPhotoSizeRef.current !== size) {
          setCameraCacheBust(Date.now());
        }
        lastPhotoSizeRef.current = size;
      } catch (err) {
        // HEAD blocked (e.g. CORS on the storage bucket) — fall back to a plain periodic reload
        // after a couple of failures instead of the image never refreshing again.
        consecutiveFailures += 1;
        if (!cancelled && consecutiveFailures >= 2) {
          setCameraCacheBust(Date.now());
          consecutiveFailures = 0;
        }
      }
    };

    checkForNewPhoto();
    const id = setInterval(checkForNewPhoto, CAMERA_PHOTO_CHECK_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [currentPhotoUrl]);

  useEffect(() => {
    if (hydrantId == null) toast.error("Hydrant Id Not set");
  }, [hydrantId]);

  useEffect(() => {
    if (Array.isArray(hydrantLists) && hydrantLists.length > 0) {
      const withVolume = hydrantLists.filter(
        (item) => item?.dateTime != null && item?.totalWaterVolume != null
      );
      const withEndVolume = hydrantLists.filter(
        (item) => item?.dateTime != null && item?.endWaterVolume != null
      );
      setGraphHeader(withVolume.map((item) => getDisplayDateTimeFormat(item?.dateTime)));
      setGraphDataTotalVolume(
        withVolume.map((item) => Number(parseFloat(item.totalWaterVolume).toFixed(2)))
      );
      setGraphHeaderEnd(withEndVolume.map((item) => getDisplayDateTimeFormat(item?.dateTime)));
      setGraphDataEndVolume(
        withEndVolume.map((item) => Number(parseFloat(item.endWaterVolume).toFixed(2)))
      );
    } else {
      setGraphHeader([]);
      setGraphHeaderEnd([]);
      setGraphDataTotalVolume([]);
      setGraphDataEndVolume([]);
    }
  }, [hydrantLists]);

  useEffect(() => {
    if (errors?.status === 401) {
      dispatch(logout());
      toast.error(errors?.data?.message);
      navigate("/login");
      window.location.reload();
    }
  }, [errors, dispatch, navigate]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (hydrantId.length > 0) {
      dispatch(
        getHydrantLiveList(
          auth?.accessToken,
          paginate,
          page,
          search,
          sort,
          hydrantId[0]?.hydrantId || hydrantId[0]?.id,
          duration,
          selectedDate
        )
      );
    } else {
      toast.error("Please select hydrant");
    }
  };

  const fetchTripReportList = useCallback(() => {
    if (hydrantId.length > 0) {
      dispatch(
        getHydrantLiveList(
          auth?.accessToken,
          paginate,
          page,
          search,
          sort,
          hydrantId[0]?.hydrantId || hydrantId[0]?.id,
          duration,
          selectedDate
        )
      );
    }
  }, [auth?.accessToken, paginate, page, search, sort, hydrantId, dispatch, duration, selectedDate]);

  useDebounce(fetchTripReportList, 500, [auth, paginate, page, search, sort]);

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      borderColor: errors?.data?.state_code ? "red" : base.borderColor,
    }),
  };

  const apexLineChartWithLabels = (categories, yTitle) => ({
    chart: {
      type: "area",
      zoom: { enabled: false },
      toolbar: { show: false },
      fontFamily: "Poppins, sans-serif",
      animations: { enabled: true, easing: "easeinout", speed: 800 },
    },
    dataLabels: { enabled: false },
    stroke: { width: [3], curve: "smooth" },
    grid: {
      row: { colors: ["transparent"], opacity: 0.2 },
      borderColor: "rgba(0, 150, 180, 0.15)",
      xaxis: { lines: { show: false } },
    },
    markers: { size: 5, hover: { size: 7 } },
    xaxis: {
      categories,
      labels: { style: { colors: "#006064", fontFamily: "Poppins" } },
      title: { text: "Date/Time", style: { color: "#00838f", fontFamily: "Poppins" } },
    },
    yaxis: {
      labels: { style: { colors: "#006064", fontFamily: "Poppins" } },
      title: { text: yTitle, style: { color: "#00838f", fontFamily: "Poppins" } },
    },
    legend: {
      position: "top",
      horizontalAlign: "right",
      fontFamily: "Poppins",
      labels: { colors: "#006064" },
    },
    tooltip: { theme: "light", style: { fontFamily: "Poppins" } },
    fill: {
      type: "gradient",
      gradient: { shadeIntensity: 1, opacityFrom: 0.45, opacityTo: 0.08, stops: [0, 100] },
    },
    responsive: [{ breakpoint: 768, options: { legend: { show: false } } }],
  });

  const chartOptionsTotalVolume = {
    ...apexLineChartWithLabels(graphHeader, "Total Water Volume (kL)"),
    colors: ["#00acc1", "#26c6da"],
  };
  const chartOptionsEndVolume = {
    ...apexLineChartWithLabels(graphHeaderEnd, "End Water Volume (kL)"),
    colors: ["#7e57c2", "#9575cd"],
  };

  const apexLineChartDataTotal = [{ name: "Total Volume (kL)", data: graphDataTotalVolume }];
  const apexLineChartDataEnd = [{ name: "End Volume (kL)", data: graphDataEndVolume }];

  const firstHydrant = Array.isArray(hydrantStatusList) && hydrantStatusList.length > 0 ? hydrantStatusList[0] : null;

  const lastBookingFromApi =
    firstHydrant?.latestFilledTanker ||
    firstHydrant?.lastBooking ||
    firstHydrant?.lastFilledTanker ||
    null;
  const lastBookingFromData = useMemo(() => {
    if (!Array.isArray(hydrantLists) || hydrantLists.length === 0) return null;
    const withClose = hydrantLists.filter((item) => item?.valveCloseTime || item?.dateTime);
    if (withClose.length === 0) return null;
    const sorted = [...withClose].sort((a, b) => {
      const tA = new Date(a.valveCloseTime || a.dateTime || 0).getTime();
      const tB = new Date(b.valveCloseTime || b.dateTime || 0).getTime();
      return tB - tA;
    });
    return sorted[0];
  }, [hydrantLists]);

  const lastBooking = lastBookingFromApi || (lastBookingFromData && {
    vehicleNumber: lastBookingFromData.vehicleNumber,
    volume: lastBookingFromData.totalWaterVolume,
    completedAt: lastBookingFromData.valveCloseTime || lastBookingFromData.dateTime,
    driver: { driverName: null },
  });

  const formatVal = (v) => {
    if (v == null || v === "" || Number.isNaN(Number(v))) return "—";
    return parseFloat(v).toFixed(2);
  };

  const tankerWiseStats = useMemo(() => {
    if (!Array.isArray(hydrantLists) || hydrantLists.length === 0) return [];
    const byVehicle = {};
    hydrantLists.forEach((row) => {
      const vehicle = row?.vehicleNumber ? String(row.vehicleNumber).trim() : null;
      if (!vehicle) return;
      if (!byVehicle[vehicle]) {
        byVehicle[vehicle] = { vehicleNumber: vehicle, trips: 0, totalWaterKl: 0 };
      }
      byVehicle[vehicle].trips += 1;
      const vol = row?.totalWaterVolume != null ? Number(row.totalWaterVolume) : 0;
      byVehicle[vehicle].totalWaterKl += vol;
    });
    return Object.values(byVehicle).sort((a, b) => b.totalWaterKl - a.totalWaterKl);
  }, [hydrantLists]);

  const tankerChartCategories = useMemo(() => tankerWiseStats.map((r) => r.vehicleNumber), [tankerWiseStats]);
  const tankerChartTripsData = useMemo(() => tankerWiseStats.map((r) => r.trips), [tankerWiseStats]);
  const tankerChartWaterData = useMemo(() => tankerWiseStats.map((r) => Number(parseFloat(r.totalWaterKl).toFixed(2))), [tankerWiseStats]);

  const tankerTripsChartOptions = useMemo(() => ({
    chart: { type: "bar", toolbar: { show: false }, fontFamily: "Poppins, sans-serif" },
    plotOptions: { bar: { borderRadius: 6, columnWidth: "60%" } },
    dataLabels: { enabled: false },
    xaxis: { categories: tankerChartCategories, labels: { style: { colors: "#006064" } } },
    yaxis: { title: { text: "Trips" }, labels: { style: { colors: "#006064" } } },
    colors: ["#26c6da"],
    grid: { borderColor: "rgba(0, 150, 180, 0.15)" },
    tooltip: { theme: "light" },
  }), [tankerChartCategories]);

  const tankerWaterChartOptions = useMemo(() => ({
    chart: { type: "bar", toolbar: { show: false }, fontFamily: "Poppins, sans-serif" },
    plotOptions: { bar: { borderRadius: 6, columnWidth: "60%" } },
    dataLabels: { enabled: false },
    xaxis: { categories: tankerChartCategories, labels: { style: { colors: "#006064" } } },
    yaxis: { title: { text: "Water (kL)" }, labels: { style: { colors: "#006064" } } },
    colors: ["#66bb6a"],
    grid: { borderColor: "rgba(0, 150, 180, 0.15)" },
    tooltip: { theme: "light" },
  }), [tankerChartCategories]);

  return (
    <div className="hydrant-dashboard">
      <PageTitle
        breadCrumbItems={[
          { label: t("Hydrant Valve Live Status"), path: "/Hydrant-Live-Status", active: true },
        ]}
        title={t("Hydrant Valve Live Status")}
      />

      {/* Filters - compact */}
      <Card className="hydrant-card hydrant-filter-card hydrant-filter-compact">
        <Card.Body className="hydrant-filter-card-body">
          <Row className="g-2 align-items-center flex-nowrap">
            <Col xs="6" sm="4" md={3} className="hydrant-filter-col">
              <Select
                options={jenOptions}
                values={selectedJen}
                name="jen_id"
                labelField="name"
                valueField="id"
                placeholder="J.E.N."
                portal={typeof document !== "undefined" ? document.body : null}
                dropdownPosition="bottom"
                onChange={(selectedData) => {
                  if (selectedData?.length > 0) {
                    setSelectedJen(selectedData);
                    setJenId(selectedData[0]?.id);
                  } else {
                    setSelectedJen([]);
                    setJenId("");
                  }
                }}
                styles={customSelectStyles}
                className="hydrant-filter-select"
              />
            </Col>
            <Col xs="6" sm="4" md={3} className="hydrant-filter-col">
              <Select
                classNamePrefix="react-select"
                name="hydrantId"
                options={hydrantOptions}
                values={hydrantId}
                valueField="id"
                labelField="name"
                placeholder="Hydrant"
                portal={typeof document !== "undefined" ? document.body : null}
                dropdownPosition="bottom"
                onChange={(selectedData) => {
                  if (selectedData?.length > 0) setHydrantId([selectedData[0]]);
                  else setHydrantId([]);
                }}
                styles={customSelectStyles}
                className="hydrant-filter-select"
              />
            </Col>
            <Col xs="6" sm="3" md={2} className="hydrant-filter-col">
              <DatePicker
                selected={selectedDate}
                onChange={handleDateChange}
                dateFormat="dd/MM/yyyy"
                maxDate={new Date()}
                className="form-control form-control-sm hydrant-filter-date"
                wrapperClassName="hydrant-datepicker-wrapper"
                popperClassName="hydrant-datepicker-popper"
                popperPlacement="bottom-start"
                withPortal
                portalId="datepicker-portal"
                showPopperArrow={false}
                placeholderText="Date"
              />
            </Col>
            <Col xs="6" sm="2" md="auto" className="hydrant-filter-col">
              {loadingHydrant ? (
                <Button size="sm" className="hydrant-btn-submit" variant="primary" disabled>
                  <Spinner className="spinner-border-sm me-1" as="span" />
                  Wait
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="hydrant-btn-submit"
                  type="button"
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={loadingHydrant}
                >
                  Submit
                </Button>
              )}
            </Col>
            <Col xs="12" md="auto" className="ms-md-auto hydrant-refresh-info d-flex align-items-center gap-2">
              <span className="hydrant-refresh-text">
                {lastRefreshedAt != null && (
                  <>
                    Last: {getDisplayDateTimeFormat(new Date(lastRefreshedAt).toISOString())}
                    {hydrantId.length > 0 && (
                      <span className="hydrant-refresh-countdown">
                        · <strong className={socket?.connected ? "text-success" : "text-muted"}>
                          {socket?.connected ? "Live" : "Reconnecting..."}
                        </strong>
                      </span>
                    )}
                  </>
                )}
                {lastRefreshedAt == null && hydrantId.length === 0 && (
                  <span className="text-muted">Select hydrant & submit</span>
                )}
              </span>
              {hydrantId.length > 0 && (
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className="hydrant-reload-btn"
                  onClick={handleReload}
                  title={socket?.connected ? "Reload via socket" : "Reload via API"}
                >
                  <FaSyncAlt />
                </Button>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {hydrantId.length > 0 && (
        <>
      {/* Summary cards */}
      <Row className="g-3 mb-3">
        <Col md={6} lg={3}>
          <Card className="hydrant-stat-card">
            <div className="water-wave-bg" aria-hidden="true" />
            <Card.Body className="stat-content d-flex align-items-center justify-content-between">
              <div>
                <div className="stat-value">
                  {summary?.todayWaterSupply != null ? Number(summary.todayWaterSupply).toFixed(2) : "—"}
                </div>
                <div className="stat-label">Today Water Supply (kL)</div>
              </div>
              <div className="stat-icon-wrap">
                <i className="fe-droplet" />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} lg={3}>
          <Card className="hydrant-stat-card">
            <div className="water-wave-bg" aria-hidden="true" />
            <Card.Body className="stat-content d-flex align-items-center justify-content-between">
              <div>
                <div className="stat-value">
                  {summary?.totalTankersFilledToday != null ? summary.totalTankersFilledToday : "—"}
                </div>
                <div className="stat-label">Tankers Filled Today</div>
              </div>
              <div className="stat-icon-wrap">
                <i className="fe-truck" />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} lg={3}>
          <Card className="hydrant-stat-card">
            <div className="water-wave-bg" aria-hidden="true" />
            <Card.Body className="stat-content d-flex align-items-center justify-content-between">
              <div>
                <div className="stat-value">
                  {summary?.totalWaterSupply != null ? Number(summary.totalWaterSupply).toFixed(2) : "—"}
                </div>
                <div className="stat-label">Total Water Supply (kL)</div>
              </div>
              <div className="stat-icon-wrap">
                <i className="fe-trending-up" />
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6} lg={3}>
          <Card className="hydrant-stat-card">
            <div className="water-wave-bg" aria-hidden="true" />
            <Card.Body className="stat-content d-flex align-items-center justify-content-between">
              <div>
                <div className="stat-value">
                  {summary?.totalTankersFilledTillDate != null ? summary.totalTankersFilledTillDate : "—"}
                </div>
                <div className="stat-label">Total Tankers Filled Till Date</div>
              </div>
              <div className="stat-icon-wrap">
                <i className="fe-bar-chart-2" />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Hydrant status + camera row */}
      {firstHydrant && (
        <Row className="g-3 mb-3">
          <Col lg={8}>
            <Card className={`hydrant-card hydrant-status-card hydrant-valve-${(firstHydrant.valve?.status || "closed").toLowerCase()}`}>
              <Card.Body className="hydrant-status-card-body">
                <div className="hydrant-status-header">
                  <h6 className="hydrant-status-title">
                    <i className="fe-map-pin me-2" />
                    {firstHydrant.hydrantName}
                  </h6>
                  <div className="hydrant-valve-status-only">
                    <span className={`hydrant-valve-badge ${(firstHydrant.valve?.status || "closed").toLowerCase()}`}>
                      {(firstHydrant.valve?.status || "CLOSED").toUpperCase()}
                    </span>
                    {firstHydrant.valve?.mode && (
                      <span className="hydrant-mode-badge ms-2">{firstHydrant.valve.mode}</span>
                    )}
                  </div>
                </div>
                
                <div className={`hydrant-info-strip ${firstHydrant.valve?.status?.toLowerCase() === "open" ? "hydrant-info-strip-active" : ""}`}>
                  <div className="hydrant-info-block">
                    <span className="hydrant-meta-label">JEN</span>
                    <span className="hydrant-meta-value hydrant-meta-bold">{firstHydrant.jenName || "—"}</span>
                  </div>
                  <div className="hydrant-info-block">
                    <span className="hydrant-meta-label">AEN</span>
                    <span className="hydrant-meta-value">{firstHydrant.aenName || "—"}</span>
                  </div>
                  <div className="hydrant-info-block">
                    <span className="hydrant-meta-label">Date</span>
                    <span className="hydrant-meta-value">{firstHydrant.date || "—"}</span>
                  </div>
                  <div className="hydrant-info-block">
                    <span className="hydrant-meta-label">Flow (Ltr/Sec)</span>
                    <span className="hydrant-meta-value">{formatVal(firstHydrant.water?.flowRate)}</span>
                  </div>
                  
                </div>
                {firstHydrant.valve?.lastDataAt && (
                  <small className="hydrant-last-data text-muted">
                    Last data: {getDisplayDateTimeFormat(firstHydrant.valve.lastDataAt)}
                  </small>
                )}
                {lastBooking && (
                  <div className="hydrant-latest-tanker">
                    <span className="hydrant-latest-tanker-title">
                      <i className="fe-truck me-1" />
                      Last Booking
                    </span>
                    <div className="hydrant-latest-tanker-body">
                      <div className="hydrant-meta-grid">
                        <div className="hydrant-meta-item">
                          <span className="hydrant-meta-label">Vehicle</span>
                          <span className="hydrant-meta-value">{lastBooking.vehicleNumber || "—"}</span>
                        </div>
                        <div className="hydrant-meta-item">
                          <span className="hydrant-meta-label">Volume (kL)</span>
                          <span className="hydrant-meta-value">{formatVal(lastBooking.volume)}</span>
                        </div>
                        <div className="hydrant-meta-item">
                          <span className="hydrant-meta-label">Completed</span>
                          <span className="hydrant-meta-value small">
                            {lastBooking.completedAt ? getDisplayDateTimeFormat(lastBooking.completedAt) : "—"}
                          </span>
                        </div>
                        <div className="hydrant-meta-item">
                          <span className="hydrant-meta-label">Driver</span>
                          <span className="hydrant-meta-value">{lastBooking.driver?.driverName || "—"}</span>
                        </div>
                      </div>
                      {lastBooking.numberPlateImageUrl && (
                        <div className="hydrant-latest-tanker-plate">
                          <span className="hydrant-meta-label d-block mb-1">Number Plate</span>
                          <img
                            src={lastBooking.numberPlateImageUrl}
                            alt="Captured number plate"
                            className="hydrant-plate-image"
                          />
                          {lastBooking.identifiedPlate && (
                            <div className="mt-1">
                              {lastBooking.identifiedPlate}
                              {lastBooking.plateMatched != null && (
                                <span
                                  className={`badge ms-2 ${lastBooking.plateMatched ? "bg-success" : "bg-danger"}`}
                                >
                                  {lastBooking.plateMatched ? "Matched" : "Not Matched"}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {firstHydrant.valve?.status?.toLowerCase() === "open" && (
                  <div className="hydrant-current-filling">
                    <span className="hydrant-current-filling-title">
                      <i className="fe-zap me-1" />
                      Current Filling
                    </span>
                    <Row className="hydrant-current-filling-row align-items-center g-3">
                      <Col xs={12} md={2} className="d-flex justify-content-center justify-content-md-start">
                        <div className="hydrant-valve-image-wrap">
                          <img
                            src="/open_volve.webp"
                            alt="Valve open"
                            className="hydrant-valve-image"
                          />
                        </div>
                      </Col>
                      <Col xs={12} md={3}>
                        <div className="hydrant-current-filling-stats">
                          <div className="hydrant-meta-item">
                            <span className="hydrant-meta-label">Vehicle</span>
                            <span className="hydrant-meta-value">{firstHydrant?.currentFilling?.vehicleNumber || "—"}</span>
                          </div>
                          <div className="hydrant-meta-item">
                            <span className="hydrant-meta-label">Filling (kL)</span>
                            <span className="hydrant-meta-value">{formatVal(firstHydrant?.currentFilling?.fillingVolume)}</span>
                          </div>
                        </div>
                      </Col>
                      <Col xs={12} md={7}>
                        <div className="hydrant-fill-progress-wrap">
                          <div className="hydrant-fill-progress-header">
                            <span className="hydrant-fill-percent">
                              {Math.min(100, Math.round((Number(firstHydrant?.currentFilling?.fillingVolume) || 0) / 4 * 100))}% filled
                            </span>
                            <span className="hydrant-fill-max">Max 4 kL</span>
                          </div>
                          <div className="hydrant-fill-progress-bar">
                            <div
                              className="hydrant-fill-progress-fill"
                              style={{
                                width: `${Math.min(100, ((Number(firstHydrant?.currentFilling?.fillingVolume) || 0) / 4) * 100)}%`,
                              }}
                            />
                          </div>
                        </div>
                      </Col>
                    </Row>
                    {firstHydrant?.currentFilling?.numberPlateImageUrl && (
                      <div className="hydrant-plate-capture mt-3">
                        <span className="hydrant-meta-label d-block mb-1">Captured Number Plate</span>
                        <div className="d-flex align-items-center flex-wrap gap-2">
                          <img
                            src={firstHydrant.currentFilling.numberPlateImageUrl}
                            alt="Captured number plate"
                            className="hydrant-plate-image"
                          />
                          {firstHydrant.currentFilling.identifiedPlate && (
                            <span>
                              {firstHydrant.currentFilling.identifiedPlate}
                              {firstHydrant.currentFilling.plateMatched != null && (
                                <span
                                  className={`badge ms-2 ${firstHydrant.currentFilling.plateMatched ? "bg-success" : "bg-danger"}`}
                                >
                                  {firstHydrant.currentFilling.plateMatched ? "Matched" : "Not Matched"}
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col lg={4}>
            <Card className="hydrant-card hydrant-camera-card">
              <Card.Body>
                <h6 className="hydrant-camera-title">
                  <i className="fe-camera me-2" />
                  Live Camera
                  {firstHydrant.camera?.status && (
                    <span className={`camera-status-badge ${firstHydrant.camera.status}`}>
                      {firstHydrant.camera.status}
                    </span>
                  )}
                </h6>
                <div className="hydrant-camera-switch-wrap">
                  <button
                    type="button"
                    className={`hydrant-camera-switch-btn ${cameraViewMode === "photo" ? "active" : ""}`}
                    onClick={() => setCameraViewMode("photo")}
                  >
                    Live Photo
                  </button>
                  <button
                    type="button"
                    className={`hydrant-camera-switch-btn ${cameraViewMode === "video" ? "active" : ""}`}
                    onClick={() => setCameraViewMode("video")}
                  >
                    Live Video
                  </button>
                </div>
                {cameraViewMode === "photo" && (
                  <>
                    {firstHydrant.camera?.lastPhotoUrl ? (
                      <div className="hydrant-camera-img-wrap">
                        <img
                          key={cameraCacheBust}
                          src={`${firstHydrant.camera.lastPhotoUrl}${firstHydrant.camera.lastPhotoUrl.includes("?") ? "&" : "?"}t=${cameraCacheBust}`}
                          alt="Hydrant camera"
                          className="hydrant-camera-img"
                        />
                      </div>
                    ) : (
                      <div className="hydrant-camera-placeholder">No image</div>
                    )}
                  </>
                )}
                {cameraViewMode === "video" && firstHydrant.camera?.cameraId && (
                  <div className="hydrant-camera-video-wrap">
                    <CPPlusLive
                      title="Live Video"
                      checkOtherOptions={false}
                      showControls
                      cameraData={{ id: firstHydrant.camera.cameraId, name: firstHydrant.hydrantName }}
                      streamUrl={`${baseURL}/streams/cam_${firstHydrant.camera.cameraId}.m3u8`}
                    />
                  </div>
                )}
                {cameraViewMode === "video" && !firstHydrant.camera?.cameraId && (
                  <div className="hydrant-camera-placeholder">Camera ID not available for video</div>
                )}
                {firstHydrant.camera?.lastConnectedAt && (
                  <small className="text-muted d-block mt-2">
                    Last connected: {getDisplayDateTimeFormat(firstHydrant.camera.lastConnectedAt)}
                  </small>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Chart + Table */}
      <Row className="g-3">
        <Col lg={6}>
          <Card className="hydrant-card hydrant-chart-card">
            <Card.Body>
              <h6 className="hydrant-section-title">Total Water Volume</h6>
              <div className="hydrant-chart-wrap" style={{ height: "320px" }}>
                <Chart
                  options={chartOptionsTotalVolume}
                  series={apexLineChartDataTotal}
                  type="area"
                  height={280}
                  width="100%"
                  className="apex-charts"
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6}>
          <Card className="hydrant-card hydrant-chart-card">
            <Card.Body>
              <h6 className="hydrant-section-title">End Water Volume</h6>
              <div className="hydrant-chart-wrap" style={{ height: "320px" }}>
                <Chart
                  options={chartOptionsEndVolume}
                  series={apexLineChartDataEnd}
                  type="area"
                  height={280}
                  width="100%"
                  className="apex-charts"
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {tankerWiseStats.length > 0 && (
        <Row className="g-3 mb-3">
          <Col lg={6}>
            <Card className="hydrant-card hydrant-tanker-stats-card">
              <Card.Body>
                <h6 className="hydrant-section-title">
                  <i className="fe-truck me-2" />
                  Trips by Tanker
                </h6>
                <div className="hydrant-chart-wrap" style={{ minHeight: "280px" }}>
                  <Chart
                    options={tankerTripsChartOptions}
                    series={[{ name: "Trips", data: tankerChartTripsData }]}
                    type="bar"
                    height={260}
                    width="100%"
                    className="apex-charts"
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={6}>
            <Card className="hydrant-card hydrant-tanker-stats-card">
              <Card.Body>
                <h6 className="hydrant-section-title">
                  <i className="fe-droplet me-2" />
                  Water Filled (kL) by Tanker
                </h6>
                <div className="hydrant-chart-wrap" style={{ minHeight: "280px" }}>
                  <Chart
                    options={tankerWaterChartOptions}
                    series={[{ name: "Water (kL)", data: tankerChartWaterData }]}
                    type="bar"
                    height={260}
                    width="100%"
                    className="apex-charts"
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      
      <Row className="g-3">
        <Col lg={12}>
          <Card className="hydrant-card">
            <Card.Body>
              <h6 className="hydrant-section-title">Filling History</h6>
              <div className="hydrant-table-wrap responsive-table" style={{ overflowY: "auto"}}>
                <Table className="mb-0 hydrant-table" size="sm" responsive>
                  <thead>
                    <tr>
                      <th>SR</th>
                      <th>Vehicle</th>
                      <th>Start (kL)</th>
                      <th>End (kL)</th>
                      <th>Total (kL)</th>
                      <th>Valve Open</th>
                      <th>Valve Close</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!Array.isArray(hydrantLists) || hydrantLists.length === 0 ? (
                      <tr>
                        <td colSpan="8">No data found</td>
                      </tr>
                    ) : (
                      hydrantLists.map((item, index) => (
                        <tr key={item.srNo ?? index}>
                          <th scope="row">{item.srNo ?? index + 1}</th>
                          <td>{item.vehicleNumber || "—"}</td>
                          <td>{item.startWaterVolume != null ? formatVal(item.startWaterVolume) : "—"}</td>
                          <td>{item.endWaterVolume != null ? formatVal(item.endWaterVolume) : "—"}</td>
                          <td>{item.totalWaterVolume != null ? formatVal(item.totalWaterVolume) : "—"}</td>
                          <td>
                            {item.valveOpenTime
                              ? getDisplayDateTimeFormat(item.valveOpenTime)
                              : "—"}
                          </td>
                          <td>
                            {item.valveCloseTime
                              ? getDisplayDateTimeFormat(item.valveCloseTime)
                              : "—"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

        </>
      )}

      {hydrantId.length === 0 && (
        <Card className="hydrant-card">
          <Card.Body className="text-center text-muted py-5">
            <i className="fe-map-pin font-24 mb-2 d-block" />
            Select J.E.N., Hydrant and click Submit to view live status and stats.
          </Card.Body>
        </Card>
      )}

      {loadingMaster && <Preloader />}
      {loadingHydrant && <Preloader />}
    </div>
  );
};

export default HydrantLiveStatusReport;
